/**
 * The CTV / OTT (connected-TV) POST-FLIGHT report as a template document.
 *
 * A view-based channel, so the report leads with the VAST-rendered impression and the video
 * completion rate (VCR) — not clicks. Everything binds to the `ctv.*` section of the data model,
 * which the generate worker fills from our own `video_delivery` store (`aggregateVideoFromStore`).
 *
 * Composed entirely from existing block manifests (rule 4 — no bespoke CTV component): KPI tiles, a
 * SeriesChart funnel, PieCharts for apps and devices, and PaginatedTables for the long-tail detail.
 *
 * The headline impression is the video START. Every breakdown (apps, device environment, the apps ×
 * device table, geo, daily) is reconciled by the builder to sum to it; geo keeps an explicit
 * "Undisclosed" / "Outside targeted area" row, and the app lists an "Other apps" remainder, rather than
 * implying the named rows are the whole buy.
 */

import type { Template } from './template-schema.js';

export const CTV_TEMPLATE: Template = {
  schemaVersion: 1,
  id: 'white-label-default-ctv',
  name: 'V2 — CTV / OTT',
  version: 1,
  status: 'draft',
  agencyId: null,
  channels: ['VIDEO'],
  lifecycle: ['POSTFLIGHT'],

  theme: {
    color: {
      primary: '#001345', accent: '#06CDF2', surface: '#FFFFFF', surfaceMuted: '#F5F5F5',
      border: '#E8E8E8', textPrimary: '#0A0A0A', textInverse: '#FFFFFF',
      headerFrom: '#000A1E', headerTo: '#00174B',
      page: '#F1F3F6', stripBorder: '#2B5FA8',
    },
    typography: { fontFamily: 'Roboto', baseSizePt: 11, scale: 1.21 },
    page: { widthIn: 8.5, heightIn: 11, marginIn: 0 },
    branding: { companyName: 'ProData Media', copyrightName: 'ProData Media' },
    iconStyle: 'v2',
    layout: { pageBackground: 'waves' },
  },

  chrome: {
    header: [{
      id: 'hdr', type: 'PageHeader',
      props: { title: 'Connected TV / OTT', showAccentRule: true },
      bindings: { clientName: 'campaign.clientName', campaignId: 'campaign.reportSo', logo: 'presentation.logo', showLogo: 'presentation.showLogo' },
    }],
    footer: [{
      id: 'ftr', type: 'PageFooter', props: {},
      bindings: {
        companyName: 'campaign.agencyName',
        year: '$substring(report.periodEnd, 0, 4)',
      },
    }],
  },

  pages: [
    // ------------------------------------------------------------- page 1 --
    {
      id: 'summary',
      mode: 'fixed',
      title: 'Campaign summary',
      blocks: [
        {
          // Creative on the left, campaign details on the right — the same order as display and audio.
          id: 'identity-row', type: 'Columns', props: { columns: 2 }, bindings: {},
          children: [
            {
              id: 'creative', type: 'CreativePreview',
              props: { maxHeightIn: 1.9, caption: '* Representative of the video creative used in campaign.' },
              bindings: { assetId: 'ctv.creativeAssetId' },
            },
            {
              id: 'meta', type: 'CampaignMetaCard',
              props: {
                variant: 'labels',
                // Render the editable "Report note" (campaign.notes) under the campaign details.
                showNote: true,
                // The name row gets the video mark so it is not a second "#" beside Campaign ID.
                nameIcon: 'kpi-video-impressions',
              },
              bindings: {
                clientName: 'campaign.clientName', campaignName: 'campaign.name',
                startDate: 'campaign.startDate', endDate: 'campaign.endDate',
                campaignId: 'campaign.reportSo',
                note: 'campaign.notes',
              },
            },
          ],
        },
        {
          id: 'kpis', type: 'KpiTileRow', props: { columns: 4 },
          bindings: {
            // Each tile gets its own V2 raster mark (not the generic glyph names, which render tiny on a
            // V2 report, nor one shared video mark). The slots are the approved raster art: video mark for
            // the impression headline, the stream/play mark for the completion rate, the impression (eye)
            // mark for completed views, and the audience mark for reach.
            tiles: `[
              {"label": "Impressions", "value": overall.delivery.impressions, "icon": "kpi-video-impressions"},
              {"label": "Video Completion Rate", "value": ctv.vcr, "icon": "kpi-total-streams", "format": "pct"},
              {"label": "Completed Views", "value": ctv.completes, "icon": "kpi-targeted-impressions"},
              {"label": "Reach", "value": ctv.reachPct, "icon": "kpi-retargeted-impressions", "format": "pct"}
            ]`,
          },
        },
        {
          // Contracted vs delivered — won impressions are the "actual" for a CTV buy.
          id: 'contracted-actual', type: 'ContractedVsActual',
          props: { contractedIcon: 'kpi-video-impressions', actualIcon: 'kpi-video-impressions' },
          bindings: { contracted: 'overall.contract.targetImpressions', actual: 'overall.delivery.impressions' },
        },
        {
          // Impressions received per day across the flight (start→end on the X axis).
          id: 'daily-card', type: 'SectionCard',
          props: { title: 'Impressions by Day', icon: 'calendar' },
          // A long flight is charted by week or month (the chart draws at most 40 points); say which.
          bindings: { title: `"Impressions by " & (ctv.impressionsSeriesUnit = "week" ? "Week" : ctv.impressionsSeriesUnit = "month" ? "Month" : "Day")` },
          children: [{
            id: 'daily-line', type: 'SeriesChart',
            props: { chartType: 'line', heightIn: 2.4, valueFormat: 'int', showLegend: false, yLabel: 'Impressions' },
            bindings: {
              categories: 'ctv.impressionsByDay.label',
              series: `[{"name": "Impressions", "values": ctv.impressionsByDay.impressions}]`,
            },
          }],
        },
      ],
    },

    // ------------------------------------------------------------- page 2 --
    {
      id: 'video-performance',
      mode: 'fixed',
      title: 'Video performance',
      blocks: [
        {
          // Impressions by day of week (Sun–Sat) × hour (0–23), shaded by volume.
          id: 'daytime-card', type: 'SectionCard',
          props: { title: 'Views by Day & Time', icon: 'calendar' }, bindings: {},
          children: [{
            id: 'daytime-heatmap', type: 'DayHourHeatmap',
            props: { caption: 'Hour of day (US Eastern) · shade = impressions' },
            bindings: { matrix: 'ctv.viewsByDayHour' },
          }],
        },
        {
          id: 'perf-trio', type: 'MetricTrio', props: {}, bindings: {
            metrics: `[
              {"label": "Video Starts", "value": ctv.starts, "icon": "kpi-total-streams"},
              {"label": "Unique Reach", "value": ctv.reach, "icon": "kpi-retargeted-impressions"},
              {"label": "Avg. Frequency", "value": ctv.averageFrequency, "icon": "kpi-retargeted-clicks", "format": "dec2"}
            ]`,
          },
        },
        {
          id: 'funnel-card', type: 'SectionCard',
          props: { title: 'Video Completion Funnel', icon: 'play' }, bindings: {},
          children: [{
            id: 'funnel', type: 'SeriesChart',
            props: { chartType: 'bar', heightIn: 2.8, valueFormat: 'int', showLegend: false, yLabel: 'Views' },
            bindings: {
              categories: 'ctv.funnel.label',
              series: `[{"name": "Views", "values": ctv.funnel.eventCount}]`,
            },
          }],
        },
      ],
    },

    // ------------------------------------------------------------- page 3 --
    // Inventory: the two pies side by side (50/50).
    // Apps / device both come from the WON impression (`impressions_v2`), which a VAST-only CTV/OTT
    // video ad never writes — so the whole page drops when neither has data, and each pie drops on its
    // own, rather than printing an empty chart. Geo (next pages) comes from VAST and always shows.
    {
      id: 'inventory',
      mode: 'flowing',
      title: 'Inventory',
      visibleWhen: '$count(ctv.apps) > 0 or $count(ctv.deviceTypes) > 0 or $count(ctv.appDevices) > 0',
      blocks: [
        {
          id: 'inventory-row', type: 'Columns', props: { columns: 2 }, bindings: {},
          children: [
            {
              id: 'apps-card', type: 'SectionCard',
              props: { title: 'Impressions by App / Channel', icon: 'monitor' }, bindings: {},
              visibleWhen: '$count(ctv.apps) > 0',
              children: [{
                id: 'apps-pie', type: 'PieChart',
                props: { heightIn: 1.9, maxSlices: 8, showLegend: true },
                bindings: { slices: `ctv.apps.{"label": name, "value": impressions}` },
              }],
            },
            {
              id: 'device-card', type: 'SectionCard',
              props: { title: 'Device Environment', icon: 'devices' }, bindings: {},
              visibleWhen: '$count(ctv.deviceTypes) > 0',
              children: [{
                id: 'device-pie', type: 'PieChart',
                props: { heightIn: 1.9, maxSlices: 6, showLegend: true },
                bindings: { slices: `ctv.deviceTypes.{"label": name, "value": impressions}` },
              }],
            },
          ],
        },
        // The apps × device table follows the pies on the same page. It used to get a page of its own (a
        // long breakable table under a tall atomic block once spilled into the footer), which left this
        // page two-thirds empty; checked against NMWRR1025's 40+ rows that it now paginates cleanly.
        {
          id: 'apps-table-card', type: 'SectionCard',
          props: { title: 'Apps & Channels by Device Type' }, bindings: {},
          // Also won-impression data — dropped for a VAST-only buy.
          visibleWhen: '$count(ctv.appDevices) > 0',
          children: [{
            id: 'apps-table', type: 'PaginatedTable',
            props: {
              columns: JSON.stringify([
                { key: 'app', label: 'App / Channel', widthPct: 44 },
                { key: 'deviceType', label: 'Device Type', widthPct: 22 },
                { key: 'platform', label: 'Platform', widthPct: 16 },
                { key: 'impressions', label: 'Impressions', widthPct: 18, format: 'int' },
              ]),
              zebra: true, maxRows: 40, showingOf: true,
              // Builder-only: each row gets a remove control keyed on the app name; removing one
              // redistributes its impressions across the rest (apply CtvRemovals).
              removeDim: 'apps', removeKeyCol: 'app',
            },
            bindings: { rows: 'ctv.appDevices', totalRows: '$count(ctv.appDevices)' },
          }],
        },
      ],
    },

    // ------------------------------------------------------------- geography --
    {
      id: 'geo-states',
      mode: 'flowing',
      title: 'Geographic distribution',
      blocks: [
        {
          // A US choropleth of impressions by state — where delivery came from — leading the
          // geographic section, ahead of the state and city breakdowns.
          id: 'geo-map-card', type: 'SectionCard',
          props: { title: 'Impressions by Geography', icon: 'pin' }, bindings: {},
          children: [
            {
              // What the campaign targeted — National / State / ZIP+radius / Geofence.
              id: 'geo-targeting', type: 'RichTextBlock', props: { size: 'lead' },
              bindings: { html: `"Targeting: " & ctv.targetingSummary` },
            },
            {
              // 4.6in keeps the box close to the Mapbox image's 3:2 shape, so a tall county is not cropped
              // top and bottom (it was at 3.4in); the page has room now the target table shares it.
              id: 'geo-map', type: 'GeoHeatMap', props: { heightIn: 4.6 },
              bindings: {
                regions: 'ctv.geoStateMap',
                geoMode: 'targeting.geoMode', states: 'targeting.states',
                zips: 'targeting.zips', radiusMiles: 'targeting.radiusMiles',
                counties: 'targeting.counties', countyLabels: 'targeting.countyLabels',
                mapImage: 'targeting.mapImage',
              },
            },
          ],
        },
        {
          id: 'states-table-card', type: 'SectionCard',
          props: { title: 'Impressions by State' }, bindings: {},
          // State breakdown only for a national buy. A targeted buy (state/ZIP/local) breaks down by city
          // within the target instead — see the geography page's city breakdown — so a national state
          // list would contradict the targeting.
          visibleWhen: "targeting.geoMode = 'country'",
          children: [{
            id: 'states-table', type: 'PaginatedTable',
            props: {
              columns: JSON.stringify([
                { key: 'name', label: 'State', widthPct: 70 },
                { key: 'impressions', label: 'Impressions', widthPct: 30, format: 'int' },
              ]),
              // Top 10 states + an "Other" row (the builder caps and labels them); 11 rows max so the
              // reconciling "Other" row is always shown. showingOf stays off-by-design: the rows already
              // sum to the total, so a "showing 11 of N" line would be misleading.
              zebra: true, maxRows: 11, showingOf: false,
            },
            bindings: { rows: 'ctv.geoStates', totalRows: '$count(ctv.geoStates)' },
          }],
        },
        // Impressions by Target (or Top Cities) follows the map on the same page rather than a page of its
        // own — a one-row target table no longer takes a whole sheet.
        {
          // Impressions attributed to the campaign's DEFINED geo targets (counties / cities / DMAs / ZIPs /
          // states from the portal). Shown on a targeted buy; the rows sum to the headline impression. Each
          // row is editable/removable (the id is a stable data path) — a removal redistributes in the
          // cascade. Replaces Top Cities when targets exist.
          id: 'geo-targets-card', type: 'SectionCard',
          props: { title: 'Impressions by Target' }, bindings: {},
          // Reviewer-controlled (Settings → Cities chart, on by default for CTV), as when it had its own page.
          visibleWhen: 'presentation.showCitiesChart = true and $count(ctv.geoTargets) > 0',
          children: [{
            id: 'geo-targets-table', type: 'PaginatedTable',
            props: {
              columns: JSON.stringify([
                { key: 'name', label: 'Target', widthPct: 70 },
                { key: 'impressions', label: 'Impressions', widthPct: 30, format: 'int' },
              ]),
              zebra: true, maxRows: 60, showingOf: false,
              // Builder-only: remove a geo target and its impressions redistribute across the rest.
              removeDim: 'geoTargets', removeKeyCol: 'id',
            },
            bindings: { rows: 'ctv.geoTargets', totalRows: '$count(ctv.geoTargets)' },
          }],
        },
        {
          // Fallback for a NATIONAL buy (no defined targets): the scoped top-10 + "Other" resolved cities.
          id: 'cities-table-card', type: 'SectionCard',
          props: { title: 'Top Cities' }, bindings: {},
          visibleWhen: 'presentation.showCitiesChart = true and $count(ctv.geoTargets) = 0',
          children: [{
            // The scoped top-10 + "Other" table is built in the generator (`ctv.topCities`) so its rows
            // are real, EDITABLE data paths. Editing a city count rolls up to the headline impression and
            // cascades to starts / completed views / reach / frequency (CTV_INVARIANTS / CTV_DERIVATIONS).
            id: 'cities-table', type: 'PaginatedTable',
            props: {
              columns: JSON.stringify([
                { key: 'name', label: 'City', widthPct: 70 },
                { key: 'impressions', label: 'Impressions', widthPct: 30, format: 'int' },
              ]),
              zebra: true, maxRows: 11, showingOf: false,
            },
            bindings: { rows: 'ctv.topCities', totalRows: '$count(ctv.topCities)' },
          }],
        },
      ],
    },
  ],

  createdAt: '2026-09-06T00:00:00.000Z',
  createdBy: 'ctv-postflight',
};
